/* eslint no-underscore-dangle: ["error", {
	"allow": ['_loaded', '_loading', '_loadedRegular', '_loadedBold', '_loadedItalic', '_loadedBoldItalic', '_attempt', '_failed']
}] */
import { FontModel } from 'interfaces/app';
import {
	ERRORS_INVALID_REQUEST_DATA,
	ERRORS_LOAD_FONT,
} from 'settings/errors';
import getBucketUrl from 'tools/get-bucket-url';
import _ from 'underscore';
import Vue from 'vue';
import {
	Action,
	Module,
	Mutation,
	VuexModule,
} from 'vuex-module-decorators';
import WebFont from 'webfontloader';

const defaultsFontModel: Partial<FontModel> = {
	_attempt: 0,
	_failed: false,
	_loaded: false,
	_loadedBold: false,
	_loadedBoldItalic: false,
	_loadedItalic: false,
	_loadedRegular: false,
	_loading: false,
};

@Module({ namespaced: true, name: 'font' })
export default class Font extends VuexModule {
	public collection: FontModel[] = [];

	public get getById() {
		return (id: string) => _.findWhere(
			this.collection,
			{ id },
		);
	}

	public get getDefault() {
		return _.findWhere(
			this.collection,
			{ isDefault: true },
		)
			|| this.getPrintable[0];
	}

	public get getPrintable() {
		return _.filter(
			this.collection,
			(model) => model.print,
		);
	}

	@Mutation
	private _addModel(data: FontModel) {
		this.collection.push(data);
	}

	@Mutation
	private _updateModel(data: Partial<FontModel>) {
		const i = _.findIndex(
			this.collection,
			{ id: data.id },
		);
		const model = this.collection[i];
		Vue.set(
			this.collection,
			i,
			_.extend(
				model,
				data,
			),
		);
	}

	@Action({ rawError: true })
	public addModel(data: OptionalExceptFor<FontModel, 'id'>): Promise<FontModel> {
		const { getters, commit, dispatch } = this.context;

		if (!data.id) {
			return Promise.reject(
				new Error(ERRORS_INVALID_REQUEST_DATA),
			);
		}

		data = _.extend(
			JSON.parse(JSON.stringify(defaultsFontModel)),
			data,
		);

		if (getters.getById(data.id)) {
			commit(
				'_updateModel',
				data,
			);
		} else {
			commit(
				'_addModel',
				data,
			);
		}

		const fontModel = getters.getById(data.id);
		if (!fontModel) {
			return Promise.reject(
				new Error('Could not find font model'),
			);
		}

		if (fontModel.autoload && !fontModel._loaded) {
			return dispatch(
				'loadModel',
				fontModel.id,
			)
				.then(() => fontModel);
		}

		return Promise.resolve(fontModel);
	}

	@Action({ rawError: true })
	public addModels(arrData: FontModel[]) {
		const { dispatch } = this.context;

		const arrPromises: Promise<FontModel>[] = [];

		arrData.forEach((data) => {
			const p = dispatch(
				'addModel',
				data,
			);
			arrPromises.push(p);
		});

		return Promise.all(arrPromises);
	}

	@Action({ rawError: true })
	public loadModel(
		id: string,
	): Promise<FontModel> {
		const {
			commit,
			dispatch,
			rootState,
		} = this.context;

		const fontModel = this.getById(id);
		if (!fontModel) {
			return Promise.reject(
				new Error('Could not find font model'),
			);
		}

		if (fontModel._loaded) {
			return Promise.resolve(
				fontModel,
			);
		}

		if (fontModel._loading) {
			// Font is already loading, wait for loaded event
			return new Promise((resolve, reject) => {
				const int = window.setInterval(
					() => {
						if (fontModel._loaded) {
							window.clearInterval(int);
							resolve(fontModel);
						} else if (fontModel._failed) {
							window.clearInterval(int);
							reject(new Error(ERRORS_LOAD_FONT));
						}
					},
					100,
				);
			});
		}

		commit(
			'_updateModel',
			{
				id: fontModel.id,
				_attempt: fontModel._failed ? 1 : fontModel._attempt + 1,
				_failed: false,
				_loading: true,
			},
		);

		return new Promise((resolve, reject) => {
			const fonts: WebFont.Config = {
				fontactive(fontFamily, fvd) {
					if (fontModel.url) {
						fontFamily = fontFamily.replace(
							' BoldItalic',
							'',
						);
						fontFamily = fontFamily.replace(
							' Bold',
							'',
						);
						fontFamily = fontFamily.replace(
							' Italic',
							'',
						);
					}

					if (fvd == 'n4') {
						// normal
						commit(
							'_updateModel',
							{
								id: fontModel.id,
								_loadedRegular: true,
							},
						);
					} else if (fvd == 'i4') {
						// italic
						commit(
							'_updateModel',
							{
								id: fontModel.id,
								_loadedItalic: true,
							},
						);
					} else if (fvd == 'n7') {
						// bold
						commit(
							'_updateModel',
							{
								id: fontModel.id,
								_loadedBold: true,
							},
						);
					} else if (fvd == 'i7') {
						// bold italic
						commit(
							'_updateModel',
							{
								id: fontModel.id,
								_loadedBoldItalic: true,
							},
						);
					}

					if (fontModel.bold && !fontModel._loadedBold) {
						// Do nothing
					} else if (fontModel.italic && !fontModel._loadedItalic) {
						// Do nothing
					} else if (fontModel.bolditalic && !fontModel._loadedBoldItalic) {
						// Do nothing
					} else if (!fontModel._loadedRegular) {
						// Do nothing
					} else {
						// Flag font as loaded after a short delay
						window.setTimeout(
							() => {
								commit(
									'_updateModel',
									{
										id: fontModel.id,
										_loaded: true,
									},
								);

								resolve(fontModel);
							},
							rootState.config['webfont.timeout'],
						);
					}
				},
				fontinactive() {
					if (fontModel._attempt === 3) {
						commit(
							'_updateModel',
							{
								id: fontModel.id,
								_loading: false,
								_failed: true,
							},
						);

						reject(new Error(ERRORS_LOAD_FONT));
					} else {
						commit(
							'_updateModel',
							{
								id: fontModel.id,
								_loading: false,
							},
						);

						// Try again
						dispatch(
							'loadModel',
							fontModel.id,
						).then(
							resolve,
							reject,
						);
					}
				},
			};

			if (fontModel.url) {
				const bucketUrl = getBucketUrl('webfonts');
				const path = `${bucketUrl}fonts/`;
				const fontUrl = fontModel.url.substring(
					0,
					4,
				) == 'http'
					? fontModel.url
					: path + fontModel.url;

				fonts.custom = {};
				fonts.custom.families = [];
				fonts.custom.urls = [];

				if (!fontModel._loadedRegular) {
					fonts.custom.families.push(`${fontModel.id}:n4`);
					fonts.custom.urls.push(fontUrl);
				}
				if (fontModel.bold && !fontModel._loadedBold) {
					fonts.custom.families.push(`${fontModel.id} Bold:n7`);
					fonts.custom.urls.push(fontUrl);
				}
				if (fontModel.italic && !fontModel._loadedItalic) {
					fonts.custom.families.push(`${fontModel.id} Italic:i4`);
					fonts.custom.urls.push(fontUrl);
				}
				if (fontModel.bolditalic && !fontModel._loadedBoldItalic) {
					fonts.custom.families.push(`${fontModel.id} BoldItalic:i7`);
					fonts.custom.urls.push(fontUrl);
				}
			} else if (fontModel.provider == 'typekit') {
				if (fontModel.kitID) {
					fonts.typekit = {
						id: fontModel.kitID,
					};
				} else {
					throw new Error('Missing font kitID');
				}
			} else if (fontModel.provider == 'google') {
				let families = `${fontModel.id}:`;
				if (!fontModel._loadedRegular) {
					families += '400,';
				}
				if (fontModel.bold && !fontModel._loadedBold) {
					families += '700,';
				}
				if (fontModel.italic && !fontModel._loadedItalic) {
					families += '400italic,';
				}
				if (fontModel.bolditalic && !fontModel._loadedBoldItalic) {
					families += '700italic,';
				}

				if (fontModel.subset && fontModel.subset != 'latin') {
					// Only at the subset to the font url if it is not just latin, otherwise it will fail
					families += `&subset=${fontModel.subset}`;
				}

				fonts.google = {
					families: [families],
				};
			}

			WebFont.load(fonts);
		});
	}
}
